import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Eye, Package, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import ProcurementService from '@/api/procurement.service';
import { canCreate, canEdit, canDelete, canView } from '@/lib/permissions';

interface GoodsReceivedVoucher {
  id: number;
  publicId: string;
  businessId: number;
  grvNumber: string;
  purchaseOrderId: number;
  purchaseOrderNumber: string;
  supplierId: number;
  supplierName: string;
  status: 'parked' | 'verified' | 'posted' | 'rejected';
  receivedDate: string;
  receivedBy?: string;
  verifiedBy?: string;
  totalQuantityOrdered: number;
  totalQuantityReceived: number;
  totalValue: number;
  currency: string;
  discrepancies?: string;
  notes?: string;
  dateCreated: string;
  dateUpdated: string;
}

const GoodsReceived: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [grvs, setGrvs] = useState<GoodsReceivedVoucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const businessId = parseInt(localStorage.getItem('selectedBusiness') ? JSON.parse(localStorage.getItem('selectedBusiness')!).id : '1');

  useEffect(() => {
    fetchGoodsReceived();
  }, [businessId]);

  const fetchGoodsReceived = async () => {
    try {
      setLoading(true);
      const response = await ProcurementService.getGoodsReceivedVouchers(businessId);
      
      if (response.data.result === 'success') {
        setGrvs(response.data?.grvs || []);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch goods received vouchers",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error fetching goods received vouchers:', error);
      toast({
        title: "Error",
        description: "Failed to fetch goods received vouchers",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (grv: GoodsReceivedVoucher, status: string) => {
    if (!canEdit('Procurement')) {
      toast({
        title: "Permission Denied",
        description: "You don't have permission to update goods received vouchers",
        variant: "destructive",
      });
      return;
    }

    try {
      await ProcurementService.updateGoodsReceivedStatus(grv.publicId, { status });
      toast({
        title: "Success",
        description: "Goods received voucher status updated successfully",
      });
      fetchGoodsReceived();
    } catch (error) {
      console.error('Error updating GRV status:', error);
      toast({
        title: "Error",
        description: "Failed to update goods received voucher status",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      parked: { variant: 'outline' as const, label: 'Parked', icon: Clock },
      verified: { variant: 'secondary' as const, label: 'Verified', icon: CheckCircle },
      posted: { variant: 'default' as const, label: 'Posted', icon: CheckCircle },
      rejected: { variant: 'destructive' as const, label: 'Rejected', icon: AlertTriangle },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.parked;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount / 100);
  };

  const getReceiptPercentage = (grv: GoodsReceivedVoucher) => {
    return grv.totalQuantityOrdered > 0 
      ? Math.round((grv.totalQuantityReceived / grv.totalQuantityOrdered) * 100)
      : 0;
  };

  const hasDiscrepancies = (grv: GoodsReceivedVoucher) => {
    return grv.totalQuantityReceived !== grv.totalQuantityOrdered || grv.discrepancies;
  };

  const filteredGrvs = grvs.filter(grv => {
    const matchesSearch = grv.grvNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         grv.purchaseOrderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         grv.supplierName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || grv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalValue = filteredGrvs.reduce((sum, grv) => sum + grv.totalValue, 0);
  const pendingCount = filteredGrvs.filter(grv => grv.status === 'parked').length;
  const discrepancyCount = filteredGrvs.filter(grv => hasDiscrepancies(grv)).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Goods Received</h1>
          <p className="text-muted-foreground">
            Manage goods received vouchers and delivery verification
          </p>
        </div>
        {canCreate('Procurement') && (
          <Button onClick={() => navigate('/procurement/goods-received/create')} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Create GRV
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total GRVs</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredGrvs.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Verification</CardTitle>
            <Clock className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">With Discrepancies</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{discrepancyCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalValue)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Search & Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search goods received vouchers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-input rounded-md"
            >
              <option value="">All Statuses</option>
              <option value="parked">Parked</option>
              <option value="verified">Verified</option>
              <option value="posted">Posted</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* GRVs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Goods Received Vouchers ({filteredGrvs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>GRV Number</TableHead>
                    <TableHead>Purchase Order</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Receipt %</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Received Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGrvs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <div className="flex flex-col items-center gap-2">
                          <Package className="h-8 w-8 text-muted-foreground" />
                          <p className="text-muted-foreground">No goods received vouchers found</p>
                          {canCreate('Procurement') && (
                            <Button onClick={() => navigate('/procurement/goods-received/create')} size="sm">
                              Create your first GRV
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredGrvs.map((grv) => (
                      <TableRow key={grv.id}>
                        <TableCell>
                          <div className="font-medium">{grv.grvNumber}</div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{grv.purchaseOrderNumber}</div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{grv.supplierName}</div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(grv.status)}
                            {hasDiscrepancies(grv) && (
                              <Badge variant="destructive" className="text-xs">
                                Discrepancy
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className={`font-medium ${
                              getReceiptPercentage(grv) === 100 ? 'text-green-600' : 
                              getReceiptPercentage(grv) >= 80 ? 'text-orange-600' : 
                              'text-red-600'
                            }`}>
                              {getReceiptPercentage(grv)}%
                            </div>
                            <div className="text-sm text-muted-foreground">
                              ({grv.totalQuantityReceived}/{grv.totalQuantityOrdered})
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{formatCurrency(grv.totalValue, grv.currency)}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{new Date(grv.receivedDate).toLocaleDateString()}</div>
                            {grv.receivedBy && (
                              <div className="text-muted-foreground">by {grv.receivedBy}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <span className="sr-only">Open menu</span>
                                <div className="h-4 w-4">⋮</div>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              {canView('Procurement') && (
                                <DropdownMenuItem onClick={() => navigate(`/procurement/goods-received/${grv.publicId}`)}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  View Details
                                </DropdownMenuItem>
                              )}
                              {canEdit('Procurement') && (
                                <>
                                  <DropdownMenuItem onClick={() => navigate(`/procurement/goods-received/${grv.publicId}/edit`)}>
                                    <Edit className="mr-2 h-4 w-4" />
                                    Edit GRV
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  {grv.status === 'parked' && (
                                    <>
                                      <DropdownMenuItem onClick={() => handleStatusUpdate(grv, 'verified')}>
                                        <CheckCircle className="mr-2 h-4 w-4" />
                                        Verify GRV
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleStatusUpdate(grv, 'rejected')} className="text-red-600">
                                        <AlertTriangle className="mr-2 h-4 w-4" />
                                        Reject GRV
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  {grv.status === 'verified' && (
                                    <DropdownMenuItem onClick={() => handleStatusUpdate(grv, 'posted')}>
                                      <CheckCircle className="mr-2 h-4 w-4" />
                                      Post to Inventory
                                    </DropdownMenuItem>
                                  )}
                                </>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => navigate(`/procurement/purchase-orders/${grv.purchaseOrderId}`)}>
                                <Package className="mr-2 h-4 w-4" />
                                View Purchase Order
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GoodsReceived; 